'use client';

import React, { useCallback, useEffect, useState } from 'react';
import { Example } from '@/types/Example';
import useZStore, { getReviewExamples } from '@/utils/useZStore';
import { Card, CardContent } from './ui/card';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
} from './ui/carousel';
import { type CarouselApi } from '@/components/ui/carousel';
import ReviewCard from './ReviewCard';
import { useGetSupabaseSession } from '@/hooks/useGetSupabaseSession';
import { trackReview } from '@/utils/track-review';
import { Plus } from 'lucide-react';
import Link from 'next/link';
import { Button } from './ui/button';

const TIME_TO_REVIEW_MS = 1000;

const ReviewExamples = () => {
  const forReviewExamples = useZStore(getReviewExamples());
  const initialized = useZStore((state) => state.initialized);
  const [api, setApi] = useState<CarouselApi | null>(null);
  const [exampleToTrack, setExampleToTrack] = useState<Example | null>(null);
  const session = useGetSupabaseSession();
  const userId = session?.user?.id;

  const logReview = useCallback(
    (emblaApi: CarouselApi) => {
      if (!emblaApi) {
        return;
      }
      const slidesInView = emblaApi.slidesInView();
      if (slidesInView.length === 0) {
        setExampleToTrack(null);
        return;
      }
      if (slidesInView.length !== 1) {
        return;
      }
      const exampleInView = forReviewExamples[slidesInView[0]];
      if (exampleInView !== exampleToTrack) {
        setExampleToTrack(exampleInView);
      }
    },
    [exampleToTrack, forReviewExamples],
  );

  useEffect(() => {
    if (!exampleToTrack) {
      return;
    }

    const trackExample = setTimeout(() => {
      trackReview(exampleToTrack.id, userId);
    }, TIME_TO_REVIEW_MS);

    return () => {
      clearTimeout(trackExample);
    };
  }, [exampleToTrack, userId]);

  useEffect(() => {
    if (!api) {
      return;
    }

    api.on('slidesInView', logReview);

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') {
        api.scrollPrev();
      }

      if (event.key === 'ArrowRight') {
        api.scrollNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [api, logReview]);

  const keyword = forReviewExamples.length === 1 ? 'example' : 'examples';

  // TO-DO: Some overlap with reviews tab in cheatsheet page when empty
  if (initialized && forReviewExamples.length === 0) {
    return (
      <main className="gcontainer mt-8 flex flex-col print:sm:pt-4">
        <div className="mx-auto w-full justify-center text-center">
          <p className="-mt-4 pb-8 text-center">{`You don't have examples to review`}</p>

          <Button asChild className="mt-8" variant="secondary">
            <Link href="/cheatsheet?filter=all">
              <Plus />
              <span>Add some examples</span>
            </Link>
          </Button>
        </div>
      </main>
    );
  }

  return (
    <main className="gcontainer mt-8 flex flex-col print:sm:pt-4">
      <div className="mx-auto w-full justify-center">
        {initialized ? (
          <>
            <p className="-mt-4 pb-8 text-center">{`${forReviewExamples.length} ${keyword} to review`}</p>
            <div className="px-12 sm:p-0">
              <Carousel
                className="mx-auto w-full sm:max-w-sm md:max-w-md lg:max-w-lg"
                setApi={setApi}
              >
                <CarouselContent className="flex gap-4">
                  {forReviewExamples.map((example, index) => (
                    <CarouselItem key={index}>
                      <div className="p-1">
                        <Card className="w-full">
                          <CardContent className="h-[300px] p-6">
                            <ReviewCard example={example} />
                          </CardContent>
                        </Card>
                      </div>
                    </CarouselItem>
                  ))}
                </CarouselContent>
                <CarouselPrevious />
                <CarouselNext />
              </Carousel>
            </div>
          </>
        ) : (
          <div className="container flex h-[300px] items-center justify-center">
            <p>Loading...</p>
          </div>
        )}
      </div>
    </main>
  );
};

export default ReviewExamples;
